import {
  describeSchema,
  type IArrayValidationDescription,
  type IObjectValidationDescription,
  type IValidationDescription,
} from '@framework/validation/describe/describeSchema.ts';
import { fromPairs } from 'lodash';
import { type ISchema } from 'yup';
import { multiSelectOptionsObjectName } from '../../schema/widgets/repository/inputs/multi-select/getMultiSelectOptionsObjectName.ts';
import { FormBuilderMetaFlags } from '../form-builder/flags/FormBuilderMetaFlags.ts';
import { deserializeFormSectionStatus } from '../status/serializeFormSectionsStatus.ts';
import {
  type ISerializedFormValue,
  type ISerializedRootFormValue,
} from './serializeFormValues.tsx';

export function deserializeFormValues(
  schema: ISchema<any>,
  serializedFormValues: ISerializedRootFormValue,
) {
  const description = describeSchema(schema, {});
  return deserializeFormRoot(description, serializedFormValues);
}

function deserializeFormRoot(
  schema: IValidationDescription,
  serialized: ISerializedRootFormValue,
): Record<string, any> | null {
  // if (isRoot && !('values' in serialized)) {
  // biome-ignore format: <tbd>
  //   throw new Error('Modulo serializzato non valido, manca il campo "values" nella root');
  // }

  if (!serialized) return null;

  const deserialized = deserializeObject(
    schema as IObjectValidationDescription,
    serialized.values,
  );
  return {
    ...deserialized,
    ...deserializeFormSectionStatus(serialized.sections),
  };
}

function deserializeObject(
  schema: IValidationDescription | null,
  values: ISerializedFormValue[] | undefined,
) {
  if (!values) return {};
  return Object.fromEntries(
    values.flatMap(serialized =>
      deserializeFormSchema(
        serialized?.name && schema?.type === 'object'
          ? schema?.schemas[serialized.name]
          : null,
        serialized,
      ),
    ),
  );
}

function deserializeArray(
  schema: IArrayValidationDescription | null,
  items: ISerializedFormValue[] | undefined,
): any[] {
  if (!items) return [];
  return items.map((item, i) => {
    if (!item || !('type' in item)) return item;
    // Se non c'è uno schema (es perché è un array vuoto), usiamo il primo
    // schema disponibile. Ne generiamo sempre _almeno uno_ con `value=undefined`
    // in `describeSchema`.
    const itemSchema = schema?.schemas[i] ?? schema?.schemas[0] ?? null;
    switch (item.type) {
      case 'object':
        return deserializeObject(itemSchema, item.values);
      case 'array':
        return deserializeArray(
          itemSchema as IArrayValidationDescription,
          item.items,
        );
      default:
        return item.value;
    }
  });
}

type IDeserializationTuple = [name: string, value: any];

function deserializeFormSchema(
  schema: IValidationDescription | null | undefined,
  serialized: ISerializedFormValue,
): IDeserializationTuple[] {
  if (!serialized || !('type' in serialized) || !serialized.name) {
    return [];
  }

  switch (serialized.type) {
    case 'object': {
      return [
        [
          serialized.name,
          deserializeObject(
            schema?.type === 'object' ? schema : null,
            serialized.values,
          ),
        ],
      ];
    }
    case 'array': {
      const tuples: IDeserializationTuple[] = [];

      const deserializedArray = deserializeArray(
        schema?.type === 'array' ? schema : null,
        serialized.items,
      );

      tuples.push([serialized.name, deserializedArray]);

      // Meccanismo per serializzare le opzioni di un widget array, con
      // opzioni _complesse_ (oggetti)
      // dato che sono registrate in un campo fratello, dobbiamo
      // gestirlo manualmente.
      deserializeSiblingMultiSelectOptions(
        schema,
        serialized.name,
        deserializedArray,
        tuples,
      );

      return tuples;
    }
    default: {
      const tuples: IDeserializationTuple[] = [];
      // Se il campo `value` è assente dalla risposta (e.g. vecchie versioni del
      // validatore serializzano `undefined` che JSON.stringify rimuove), trattiamo
      // il valore come `null` per evitare che `schema.cast(undefined)` ripristini
      // un default indesiderato (e.g. UUID dell'upload widget via parentWhen).
      const value = serialized.value ?? null;
      tuples.push([serialized.name, value]);

      // Meccanismo per serializzare le opzioni di un widget array;
      // dato che sono registrate in un campo fratello, dobbiamo
      // gestirlo manualmente.
      deserializeSiblingMultiSelectOptions(
        schema,
        serialized.name,
        value,
        tuples,
      );

      return tuples;
    }
  }
}

/**
 * @param tuples [side effect] Array di tuple deserializzate: se sono presenti
 * opzioni serializzate in un oggetto fratello, vengono aggiunte qui, nel
 * formato [`widget_code_options@internal`, { optionKey: boolean }]
 */
export function deserializeSiblingMultiSelectOptions(
  schema: IValidationDescription | null | undefined,
  name: string | null | undefined,
  deserializedOptions: any[],
  tuples: IDeserializationTuple[],
): void {
  if (!schema) return;

  if (!name) {
    // biome-ignore format: log
    console.warn('Impossibile deserializzare le opzioni di un multi-select senza nome');
    return;
  }

  const { serializeOptions, serializeOptionsKeyField } =
    getSerializedSiblingOptionsConfig(schema);

  if (!serializeOptions) return;

  const optionsObject = fromPairs(
    serializeOptions.map(option => [option, false]),
  );

  for (const selected of deserializedOptions ?? []) {
    // Supportiamo opzioni semplici (stringhe) o oggetti a un livello di profondità
    const optionKey = serializeOptionsKeyField
      ? selected[serializeOptionsKeyField]
      : selected;

    optionsObject[optionKey] = true;
  }

  tuples.push([multiSelectOptionsObjectName(name), optionsObject]);
}

function getSerializedSiblingOptionsConfig(schema: IValidationDescription): {
  serializeOptions: string[] | undefined;
  serializeOptionsKeyField: string | undefined;
} {
  const arraySchema = schema?.type === 'array' ? schema : null;
  const serializeOptions = arraySchema?.desc.spec.meta?.[
    FormBuilderMetaFlags.SerializeSiblingOptions
  ] as string[] | undefined;
  const serializeOptionsKeyField = arraySchema?.desc.spec.meta?.[
    FormBuilderMetaFlags.SerializeSiblingOptionsKeyField
  ] as string | undefined;

  return { serializeOptions, serializeOptionsKeyField };
}
